(ns app.domain.import.model
  (:require [app.domain.group.model :as group-model]
            [app.service.spreadsheet.model :as spreadsheet-model]
            [clojure.set :as set]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [schema-tools.core :as st]
            [schema.core :as s]))

(def import-types
  ;; The order is relevant!
  [:import-session.type/workspace
   :import-session.type/property
   :import-session.type/property-attributes
   :import-session.type/property-attachment
   :import-session.type/licence
   :import-session.type/contractor-registration
   :import-session.type/inspection
   :import-session.type/attachment])

(defn- has-feature-fn
  [feature]
  (fn [group]
    (group-model/has-feature? group feature)))

(defn- has-permit-types-fn
  [permit-types]
  (fn [group]
    (contains? permit-types (group-model/primary-permit-type group))))

(def prerequisites
  {:import-session.type/workspace               (every-pred
                                                  group-model/application-or-upper-tier-authority?
                                                  (complement (has-permit-types-fn #{:permit-type/L :permit-type/CR})))
   :import-session.type/property                (every-pred
                                                  group-model/domain?
                                                  (has-feature-fn :group.feature/property-management))
   :import-session.type/property-attributes     (every-pred
                                                  group-model/domain?
                                                  (has-feature-fn :group.feature/property-management))
   :import-session.type/property-attachment     (every-pred
                                                  group-model/domain?
                                                  (has-feature-fn :group.feature/property-management))
   :import-session.type/attachment              (every-pred
                                                  group-model/application-or-upper-tier-authority?
                                                  (has-feature-fn :group.feature/attachment-import))
   :import-session.type/licence                 (every-pred
                                                  group-model/application-or-upper-tier-authority?
                                                  (has-permit-types-fn #{:permit-type/L}))
   :import-session.type/contractor-registration (every-pred
                                                  group-model/application-or-upper-tier-authority?
                                                  (has-permit-types-fn #{:permit-type/CR}))
   :import-session.type/inspection              (every-pred
                                                  group-model/application-or-upper-tier-authority?
                                                  (some-fn (has-feature-fn :group.feature/application-inspections)
                                                           (has-feature-fn :group.feature/execution-inspections)))})

(defn basic-property-import?
  [import-type group property-management-flag?]
  (and property-management-flag?
       (= import-type :import-session.type/property)
       (group-model/domain? group)))

(defn available-import-types
  [group property-management-flag? & [property-attachment-import-flag?]]
  (filterv (fn [import-type]
             (let [prerequisite? (if-let [prereq-fn (prerequisites import-type)]
                                   (prereq-fn group)
                                   true)]
               (or (basic-property-import? import-type group property-management-flag?)
                   (and prerequisite?
                        (or (not= import-type :import-session.type/property-attachment)
                            property-attachment-import-flag?)))))
           import-types))

(def party-mapping-opts
  {:type      :text
   :required? false
   :unique?   true})

(def party-attrs
  ["first-name"
   "last-name"
   "street-address"
   "unit-number"
   "postal-code"
   "municipality"
   "province"
   "corporation-or-partnership"
   "mobile-phone"
   "other-phone"
   "email"])

(defn party-mapping-key [party-kind attr]
  (keyword "data-import.mapping"
           (str party-kind "-" attr)))

(def party-mappings
  (->> party-attrs
       (mapcat (fn [attr]
                 [[(party-mapping-key "applicant" attr)
                   (merge party-mapping-opts
                          {:group      :data-import/applicant-party
                           :hidden-for #{:permit-type/CE}})]
                  [(party-mapping-key "owner" attr)
                   (merge party-mapping-opts
                          {:group :data-import/owner-party})]
                  [(party-mapping-key "builder" attr)
                   (merge party-mapping-opts
                          {:group      :data-import/builder-party
                           :hidden-for #{:permit-type/CE}})]]))))

(def licence-party-kinds [:holder])
(def cr-party-kinds [:holder])
(def business-party-attrs [:business/names
                           :business/trade-names
                           :business/id
                           :business/personal-id
                           :business/phone
                           :business/email
                           :business/mailing-address
                           :business/municipality
                           :business/subdivision
                           :business/postal-code
                           :business/type])

(defn- make-mappings
  [kinds attrs]
  (->> (for [kind kinds attr attrs]
         [(party-mapping-key (name kind) (str (namespace attr) "-" (name attr))) attr])
       (into {})))

(def licence-party-mappings (make-mappings licence-party-kinds business-party-attrs))
(def cr-party-mappings (make-mappings cr-party-kinds business-party-attrs))

(def licence-cr-required-business-keys
  #{:data-import.mapping/holder-business-names})

(defn licence-party-fields-for-kind
  [fields kind]
  (let [mappings (make-mappings [kind] business-party-attrs)]
    (-> fields
        (select-keys (keys mappings))
        (set/rename-keys mappings))))

(def mappings
  {:import-session.type/property-attributes
   (concat
     [[:property-attribute/roll-number {:type      :text
                                        :required? true
                                        :unique?   true}]
      [:data-import.mapping/municipality {:type      :text
                                          :required? false
                                          :unique?   true}]
      [:data-import.mapping/property-attribute {:type      :property-attribute
                                                :required? true
                                                :unique?   false}]])
   :import-session.type/property
   (concat
     [[:property/id {:type      :text-only
                     :required? true
                     :unique?   true}]
      [:property/address {:type      :text
                          :required? true
                          :unique?   true}]
      [:data-import.mapping/property-state {:type      :text
                                            :required? true
                                            :unique?   true}]
      [:data-import.mapping/municipality {:type      :text
                                          :required? false
                                          :unique?   true}]
      [:property/parent-id {:type      :text
                            :required? false
                            :unique?   true}]
      [:property/parent-relationship-type {:type      :text
                                           :required? false
                                           :unique?   true}]
      [:property/type-code {:type      :text
                            :required? false
                            :unique?   true}]
      [:property/integration-id {:type      :text
                                 :required? false
                                 :unique?   true}]
      [:property/secondary-id {:type      :text
                               :required? false
                               :unique?   true}]
      [:property/zoning {:type      :text
                         :required? false
                         :unique?   true}]
      [:property/legal {:type      :text
                        :required? false
                        :unique?   true}]
      [:property/ward {:type      :text
                       :required? false
                       :unique?   true}]
      [:data-import.mapping/location-center-x {:type      :number
                                               :required? false
                                               :unique?   true}]
      [:data-import.mapping/location-center-y {:type      :number
                                               :required? false
                                               :unique?   true}]
      [:data-import.mapping/property-attribute {:type      :property-attribute
                                                :required? false
                                                :unique?   false}]])
   :import-session.type/workspace
   (concat
     [[:workspace/municipal-case-id {:type      :text-only
                                     :required? true
                                     :unique?   true}]
      [:workspace/address {:type      :text
                           :required? true
                           :unique?   true}]

      [:data-import.mapping/property-unit-designator
       {:type      :text
        :required? false
        :unique?   true}]

      [:data-import.mapping/property-unit-identifier
       {:type      :text
        :required? false
        :unique?   true}]

      [:property/id {:type      :text-only
                     :required? true
                     :unique?   true}]
      [:data-import.mapping/permit-description {:type       :text
                                                :required?  false
                                                :unique?    true
                                                :hidden-for #{:permit-type/CE}}]
      [:workspace/case-description {:type      :text
                                    :required? false
                                    :unique?   true
                                    :shown-for #{:permit-type/CE}}]
      [:workspace/municipality {:type      :text
                                ;; can auto-detect if only one
                                :required? false
                                :unique?   true}]
      [:workspace/category {:type         :text
                            :required?    false
                            :required-for #{:permit-type/CE}
                            :unique?      true}]
      [:application/work-type {:type       :text
                               :required?  false
                               :unique?    true
                               :hidden-for #{:permit-type/CE}}]
      [:application/work-target {:type       :text
                                 :required?  false
                                 :unique?    true
                                 :hidden-for #{:permit-type/CE}}]
      [:workspace/assignee {:type      :text
                            :required? false
                            :unique?   true}]
      [:data-import.mapping/workspace-status {:type      :text
                                              :required? false
                                              :unique?   true}]
      [:data-import.mapping/created {:type      :date-time
                                     :required? false
                                     :unique?   true
                                     :shown-for #{:permit-type/CE}}]
      [:data-import.mapping/creator {:type      :text
                                     :required? false
                                     :unique?   true
                                     :shown-for #{:permit-type/CE}}]
      [:application/submitted {:type       :date-time
                               :required?  false
                               :unique?    true
                               :hidden-for #{:permit-type/CE}}]
      [:application/completed {:type                    :date-time
                               :required?               false
                               :unique?                 true
                               :shown-for               #{:permit-type/LU :permit-type/P}
                               :enabled-by-feature-flag :feature-flag.id/add-planning-dates-to-import}]
      [:application/decision-made {:type                    :date-time
                                   :required?               false
                                   :unique?                 true
                                   :shown-for               #{:permit-type/LU :permit-type/P}
                                   :enabled-by-feature-flag :feature-flag.id/add-planning-dates-to-import}]
      [:verdict/issue-date {:type       :date-time
                            :required?  false
                            :unique?    true
                            :hidden-for #{:permit-type/CE}}]
      [:verdict/issuer {:type       :text
                        :required?  false
                        :unique?    true
                        :hidden-for #{:permit-type/CE}}]
      [:property/zoning {:type       :text
                         :required?  false
                         :unique?    true
                         :hidden-for #{:permit-type/CE}}]
      [:property/legal {:type      :text
                        :required? false
                        :unique?   true}]
      [:data-import.mapping/construction-cost {:type       :number
                                               :required?  false
                                               :unique?    true
                                               :hidden-for #{:permit-type/CE}}]
      [:data-import.mapping/form-field {:type      :text
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/form-field {:type      :number
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/date-field {:type      :date-time
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/date-field {:type      :date
                                        :required? false
                                        :unique?   false}]]
     party-mappings)

   :import-session.type/inspection
   [[:workspace/municipal-case-id {:type      :text
                                   :required? true
                                   :unique?   true
                                   :title     :data-import/inspection-target}]
    [:data-import.mapping/inspection-type {:type      :text
                                           :required? true
                                           :unique?   true}]
    [:inspection/priority {:type                     :text
                           :required?                false
                           :unique?                  true
                           :enabled-by-group-feature :group.feature/inspection-priority
                           :enabled-by-feature-flag  :feature-flag.id/inspection-priority}]
    [:inspection.visit/result {:type                       :text
                               :required?                  true
                               :unique?                    true
                               :deprecated-by-feature-flag :feature-flag.id/inspection-visit-configuration}]
    [:inspection.visit/visit-result {:type                    :text
                                     :required?               true
                                     :unique?                 true
                                     :enabled-by-feature-flag :feature-flag.id/inspection-visit-configuration}]
    [:inspection.visit/notes {:type      :text
                              :required? false
                              :unique?   true}]
    [:inspection.visit/internal-notes {:type      :text
                                       :required? false
                                       :unique?   true}]
    [:inspection.visit/start-time {:type      :date-time
                                   :required? false
                                   :unique?   true}]
    [:inspection.visit/inspectors {:type      :text
                                   :required? false
                                   :unique?   true}]
    [:booking/requested-start-time {:type      :date-time
                                    :required? false
                                    :unique?   true}]
    [:booking/actual-start-time {:type      :date-time
                                 :required? false
                                 :unique?   true}]]
   :import-session.type/attachment
   [[:attachment.version.file/filename {:type      :text
                                        :required? true
                                        :unique?   true}]
    [:workspace/municipal-case-id {:type      :text
                                   :required? true
                                   :unique?   true
                                   :title     :data-import/attachment-target}]
    [:attachment/types {:type      :text
                        :required? true
                        :unique?   true}]
    [:attachment/description-optional {:type      :text
                                       :required? false
                                       :unique?   true}]]
   :import-session.type/property-attachment
   [[:attachment.version.file/filename {:type      :text
                                        :required? true
                                        :unique?   true}]
    [:property/id {:type      :text-only
                   :required? true
                   :unique?   true}]
    [:data-import.mapping/municipality {:type      :text
                                        :required? false
                                        :unique?   true}]
    [:attachment/types {:type      :text
                        :required? true
                        :unique?   true}]
    [:attachment/description-optional {:type      :text
                                       :required? false
                                       :unique?   true}]]
   :import-session.type/licence
   (concat
     [[:licence/id {:type      :text
                    :required? true
                    :unique?   true}]
      [:workspace/address {:type      :text
                           :required? false
                           :unique?   true}]
      [:property/id {:type      :text-only
                     :required? false
                     :unique?   true}]
      [:data-import.mapping/municipality {:type      :text
                                          ;; can auto-detect if only one
                                          :required? false
                                          :unique?   true}]
      [:licence/category-group-config {:type      :text
                                       :required? true
                                       :unique?   true}]
      [:data-import.mapping/licence-status {:type      :text
                                            :required? false
                                            :unique?   true}]
      [:verdict/issue-date {:type      :date-time
                            :required? true
                            :unique?   true}]
      [:licence/valid-from {:type      :date-time
                            :required? true
                            :unique?   true}]
      [:licence/valid-to {:type      :date-time
                          :required? false
                          :unique?   true}]
      [:verdict/issuer {:type      :text
                        :required? false
                        :unique?   true}]
      [:property/legal {:type      :text
                        :required? false
                        :unique?   true}]
      [:workspace/assignee {:type      :text
                            :required? false
                            :unique?   true}]
      [:application/work-type {:type      :text
                               :required? false
                               :unique?   true}]
      [:application/work-target {:type      :text
                                 :required? false
                                 :unique?   true}]
      [:data-import.mapping/form-field {:type      :text
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/form-field {:type      :number
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/date-field {:type      :date-time
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/date-field {:type      :date
                                        :required? false
                                        :unique?   false}]]
     (->> licence-party-mappings
          keys
          (map (fn [field]
                 [field (merge party-mapping-opts
                               {:group     :data-import/holder-business-party
                                :required? (contains? licence-cr-required-business-keys field)})]))))
   :import-session.type/contractor-registration
   (concat
     [[:workspace/municipal-case-id {:type      :text
                                     :required? true
                                     :unique?   true}]
      [:data-import.mapping/municipality {:type      :text
                                          :required? false
                                          :unique?   true}]
      [:data-import.mapping/cr-trades {:type      :text
                                       :required? true
                                       :unique?   true}]
      [:data-import.mapping/cr-status {:type      :text
                                       :required? true
                                       :unique?   true}]
      [:verdict/issue-date {:type      :date-time
                            :required? true
                            :unique?   true}]
      [:contractor-registration/valid-to {:type      :date-time
                                          :required? false
                                          :unique?   true}]
      [:contractor-registration/valid-from {:type      :date-time
                                            :required? true
                                            :unique?   true}]
      [:verdict/issuer {:type      :text
                        :required? false
                        :unique?   true}]
      [:data-import.mapping/form-field {:type      :text
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/form-field {:type      :number
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/date-field {:type      :date-time
                                        :required? false
                                        :unique?   false}]
      [:data-import.mapping/date-field {:type      :date
                                        :required? false
                                        :unique?   false}]]
     (->> cr-party-mappings
          keys
          (map (fn [field]
                 [field (merge party-mapping-opts
                               {:group     :data-import/holder-business-party
                                :required? (contains? licence-cr-required-business-keys field)})]))))})

(s/defschema MappingDescription
  [(s/one s/Keyword "mapping identifier")
   (s/one (st/merge
            {:type      (s/enum :text :text-only :date :number :date-time :property-attribute)
             :required? s/Bool
             :unique?   s/Bool}
            (st/optional-keys
              {:group                      s/Keyword
               :hidden-for                 #{s/Keyword}
               :shown-for                  #{s/Keyword}
               :required-for               #{s/Keyword}
               :optional-for               #{s/Keyword}
               ;; Alternative ui title
               :title                      s/Keyword
               :deprecated-by-feature-flag s/Keyword
               :enabled-by-feature-flag    s/Keyword
               :enabled-by-group-feature   s/Keyword}))
          "mapping options")])

(def valid-mapping-specification!
  (s/validator {(apply s/enum import-types) [MappingDescription]}))

;; TODO should this be a test instead?
(valid-mapping-specification! mappings)

(def page-size 100)

(def max-visible-errors 10)

(def excel-cols
  (seq "ABCDEFGHIJKLMNOPQRSTUVWXYZ"))

(def error-report-formats spreadsheet-model/file-formats)

(def max-col
  (count excel-cols))

(defn excel-col-name
  ([col-nr] (excel-col-name "" col-nr))
  ([suffix col-nr]
   (when col-nr
     (let [new-suffix (nth excel-cols (mod col-nr max-col))
           suffix     (str new-suffix suffix)]
       (if (>= col-nr max-col)
         (excel-col-name suffix
                         (dec (int (/ col-nr max-col))))
         suffix)))))

(defn error? [error-entity]
  (and (eu/is-type? :entity.type/import-session-error error-entity)
       (or (= (:import-session-error/level error-entity) :import-session-error.level/error)
           (= (:import-session-error/level error-entity) nil))))

(defn warning? [error-entity]
  (and (eu/is-type? :entity.type/import-session-error error-entity)
       (= (:import-session-error/level error-entity) :import-session-error.level/warning)))

(defn error-level [error-entity]
  (cond
    (error? error-entity) :import-session-error.level/error
    (warning? error-entity) :import-session-error.level/warning))

(defn error-sort [e1 e2]
  (cond
    (= (error-level e1) (error-level e2))
    (compare (:import-session-error/row e1) (:import-session-error/row e2))

    (error? e1) -1
    (error? e2) 1

    :else 0))

(defn- value-for-mapping
  "Returns the value for the given mapping, taking into account the
  permit-type specific black- and whitelists if they have been defined.
  Note: uses primary-permit-type since this is the only one currently supported by importing"
  [primary-permit-type mapping-opts plain-key whitelist-key blacklist-key default-value]
  (let [whitelist   (some-> mapping-opts (get whitelist-key) set)
        blacklist   (some-> mapping-opts (get blacklist-key) set)
        plain-value (if plain-key
                      (get mapping-opts plain-key default-value)
                      true)]
    (cond
      whitelist (boolean (whitelist primary-permit-type))
      (and blacklist (blacklist primary-permit-type)) false
      :else (boolean plain-value))))

(defn shown-mapping?
  "Returns true if the given import mapping is visible for any of the given permit types"
  [permit-type mapping-opts]
  (value-for-mapping permit-type mapping-opts nil :shown-for :hidden-for true))

(defn required-mapping?
  "Returns true if the given import mapping is required for any of the given permit types"
  [permit-type mapping-opts]
  (value-for-mapping permit-type mapping-opts :required? :required-for :optional-for false))

(defn feature-flag-compatible-mapping?
  "Returns true if the given import mapping is enabled according to the given feature flags.
  A mapping without flag settings is enabled by default."
  [enabled-flags [_ {:keys [deprecated-by-feature-flag enabled-by-feature-flag]}]]
  (cond
    deprecated-by-feature-flag (-> enabled-flags
                                   (contains? deprecated-by-feature-flag)
                                   not)
    enabled-by-feature-flag    (-> enabled-flags
                                   (contains? enabled-by-feature-flag))
    :else                      true))

(defn group-feature-compatible-mapping?
  "Returns true if the given import mapping is enabled according to the given group features."
  [group-features [_ {:keys [enabled-by-group-feature]}]]
  (or (nil? enabled-by-group-feature)
      (contains? group-features enabled-by-group-feature)))

(defn allowed-workspace-statuses [root-config-target]
  (case root-config-target
    :permit-type/B   [:execution.state/permit-issued
                      :execution.state/construction-started
                      :execution.state/occupied
                      :execution.state/finished]
    :permit-type/CE  [:execution.state/new-code-enforcement
                      :execution.state/active-code-enforcement
                      :execution.state/closed-code-enforcement]
    :permit-type/P   [:execution.state/finished
                      :execution.state/permit-issued
                      :execution.state/inspections-started
                      :execution.state/approved
                      :execution.state/approved-in-principle
                      :state.application/refused]
    :permit-type/PW  [:execution.state/permit-issued
                      :execution.state/construction-started
                      :execution.state/control
                      :execution.state/finished]
    :permit-type/PWO [:execution.state/submitted-work-order
                      :execution.state/in-progress-work-order
                      :execution.state/completed-work-order
                      :execution.state/blocked-work-order
                      :execution.state/cancelled-work-order]
    :permit-type/LU  [:execution.state/permit-issued
                      :execution.state/inspections-started
                      :execution.state/finished]
    ;; Empty list as default so changing the page doesn't cause an error
    []))

(def mapped-fields
  {:import-session.type/licence
   #{:licence/category-group-config
     :application/work-type
     :application/work-target
     :workspace/assignee
     :data-import.mapping/municipality
     :data-import.mapping/licence-status
     :data-import.mapping/holder-business-type
     :data-import.mapping/holder-business-subdivision}
   :import-session.type/contractor-registration
   #{:data-import.mapping/cr-status
     :data-import.mapping/cr-trades
     :data-import.mapping/municipality
     :data-import.mapping/holder-business-type
     :data-import.mapping/holder-business-subdivision}
   :import-session.type/property
   #{:data-import.mapping/municipality
     :data-import.mapping/property-state
     :data-import.mapping/location-center-x
     :data-import.mapping/location-center-y}
   :import-session.type/property-attributes
   #{:data-import.mapping/municipality}
   :import-session.type/property-attachment
   #{:data-import.mapping/municipality}
   #__})

(def multiple-value-mapping? #{:data-import.mapping/cr-trades})

(defn required-fields
  [session-type permit-type]
  (->> (get mappings session-type)
       (keep (fn [[field opts]]
               (when (required-mapping? permit-type opts)
                 field)))
       set))

(defn unique-fields
  [session-type]
  (->> (get mappings session-type)
       (keep (fn [[field opts]]
               (when (:unique? opts)
                 field)))
       set))

(defn plain-fields [session-type]
  (set/difference (unique-fields session-type) (mapped-fields session-type)))

(def ^{:arglists '([session])} get-session-action-log
  (child/child-entities-of-type-fn :entity.type/import-session-action-log))

(def max-allowed-rows 1000)

(defn type-match? [types allowed-types]
  (-> (set/intersection types allowed-types) seq))

(defn default-matching-types-fn [type]
  (fn [{:keys [types]}]
    (case type
      :text-only (type-match? types #{:text})
      :text (type-match? types #{:text :number})
      :date-time (type-match? types #{:date :date-time})
      (contains? types type))))

(defn property-attribute-matching-types-fn [type]
  (fn [{:keys [types]}]
    (case type
      :property-attribute-template.type/select (type-match? types #{:text :number})
      :property-attribute-template.type/integer (type-match? types #{:number})
      :property-attribute-template.type/decimal (type-match? types #{:number})
      :property-attribute-template.type/text (type-match? types #{:text :number})
      :property-attribute-template.type/toggle (type-match? types #{:text :number})
      :property-attribute-template.type/date (type-match? types #{:date :date-time})
      :property-attribute-template.type/date-range (type-match? types #{:date :date-time})
      (contains? types type))))

(def valid-coordinate-reference-systems
  ["epsg:3857"
   "wgs84"
   "nad83"])

(defn valid-coordinate-reference-system?
  "Returns true if the given coordinate reference system is allowed in import"
  [csr]
  (some #(= % csr) valid-coordinate-reference-systems))
